import React, { useEffect, useState } from 'react';
import { Button, MenuItem, TextField, useTheme } from '@mui/material';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import moment from 'moment-timezone';

import { ChevronFilledLeftIcon, ChevronFilledRightIcon, Select } from 'src/shared/components/index';
import { DateRangeType } from 'src/shared/types';
import './DateRangeSelector.styles.scss';

interface Props<T extends DateRangeType> {
  dateRange: T;
  onDateRangeChange: (dateRange: T) => void;
  formatter?: (dateRange: T) => string;
  ranges?: T[];
  selectable?: boolean;
  type?: 'daily' | 'monthly';
  className?: string;
}

export const DateRangeSelector = <T extends DateRangeType>({
  dateRange,
  onDateRangeChange,
  formatter = ({ startDate, endDate }: DateRangeType) => `${moment(startDate).format()} - ${moment(endDate).format()}`,
  ranges = [],
  selectable,
  type = 'daily',
  className = '',
}: Props<T>) => {
  const theme = useTheme();
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const hasSelectableItems = selectable && !!ranges.length;

  useEffect(() => {
    if (!ranges?.length) {
      return;
    }

    const newIndex = ranges.findIndex((period) => isEqual(dateRange, period)) || 0;
    setCurrentIndex(newIndex);
  }, [dateRange, ranges]);

  const handleChange = (index: number, direction = 1) => {
    const newRange = hasSelectableItems ? ranges[index] : getNewRange(dateRange, direction, type);

    if (!newRange) {
      return;
    }

    onDateRangeChange({ ...newRange });
    setCurrentIndex(index);
  };

  return (
    <div className={`d-flex align-items-center justify-content-start ${className}`}>
      <Button
        size="small"
        className={`date-range-selector-btn border ${theme.palette.mode}`}
        variant="contained"
        color="info"
        disabled={hasSelectableItems && currentIndex + 1 >= ranges.length}
        startIcon={<ChevronFilledLeftIcon />}
        onClick={() => handleChange(currentIndex + 1, 1)}
        classes={{ startIcon: 'm-0' }}
      />
      {hasSelectableItems ? (
        <Select
          className={`date-range-input ${theme.palette.mode}`}
          value={currentIndex}
          onChange={(event) => handleChange(+event.target.value)}
        >
          {ranges.map((range, index) => (
            <MenuItem key={`timecard-selector-item-${index}`} value={index}>
              {formatter(range)}
            </MenuItem>
          ))}
        </Select>
      ) : (
        <TextField
          className={`date-range-input ${theme.palette.mode}`}
          value={formatter(dateRange)}
          size="small"
          style={{ width: `${formatter(dateRange).length * 0.9}ch` }}
          InputProps={{
            readOnly: true,
          }}
        />
      )}
      <Button
        size="small"
        className={`date-range-selector-btn border ${theme.palette.mode}`}
        variant="contained"
        color="info"
        disabled={hasSelectableItems && currentIndex - 1 < 0}
        startIcon={<ChevronFilledRightIcon />}
        onClick={() => handleChange(currentIndex - 1, -1)}
        classes={{ startIcon: 'm-0' }}
      />
    </div>
  );
};

const getNewRange = <T extends DateRangeType>(dateRange: T, direction: number, type?: 'daily' | 'monthly'): T => {
  const momentStartDate = moment(dateRange.startDate);
  const momentEndDate = moment(dateRange.endDate);
  const range = type === 'daily' ? momentStartDate.diff(momentEndDate, 'days') - 1 : -1;
  const unit = type === 'daily' ? 'days' : 'months';

  return {
    ...dateRange,
    startDate: cloneDeep(momentStartDate)
      .add(direction * range, unit)
      .format(),
    endDate: cloneDeep(momentEndDate)
      .add(direction * range, unit)
      .format(),
  };
};
